import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OPPORTUNITY_HEADLINE_NOTE, crmActivityCreate, crmDealCreate, crmFacilityCall, tgEhr, tgSnooze, tgWarmth, type ApiError, type CatalogRow, type CrmCompanyRecord } from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { ErrorNote } from '../../../ErrorNote';
import { ConfirmDialog } from '../../../ui/ConfirmDialog';
import { chip, figure as fig, segSmall as seg } from '../../../ui/designStyles';
import { useSales } from '../context';
import { ck, sx } from '../dc';
import { MapView, type MapHandle, type MapPin } from './MapView';
import { NetView, type NetModel, type NetNode } from './NetView';
import { OpportunityBreakdown } from './OpportunityBreakdown';
import { DIMS, TC, TIER_TONE, axis, boardMeta, buildProspects, metroLabel, money, stripOf, tc, whyNow, type Prospect, type Tier } from './model';
import { useFacilitySearch, useMetroData, useOutsideFacility, useSelectedData } from './useTargeting';
import { BuildLabel, BuildTagged } from '../../../ui/buildLabels';

// design salesVals btn() (L20038): the Sales page's own button (13px padding).
const btn = (primary: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');

const TYPES = ['All', 'SNF', 'RES'];
const TIERS = ['All', 'A', 'B', 'C'];
const VIEWS = ['Map', 'Network', 'Ranked', 'Matrix'] as const;
type View = (typeof VIEWS)[number];
const LISTS = ['Priority', 'Uncaptured $', 'Skip for now'] as const;
type ListMode = (typeof LISTS)[number];

const clock = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const warmNote = (w: number) => (w ? (w >= 4 ? 'warm' : w >= 2 ? 'lukewarm' : 'cold') : 'not set');
const warmBtn = (n: number, w: number, wrap: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;flex:0 0 26px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;' +
  (wrap ? 'white-space:nowrap;' : '') +
  (n <= w ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.16);');

/** A facility outside the metro lists (a search hit, or a signal for another metro), as a prospect with no score breakdown. */
function outsider(id: string, hit: CatalogRow | null, rec: CrmCompanyRecord | null): Prospect | null {
  if (hit) {
    const od = hit.open_dollars;
    return {
      key: id, name: tc(hit.name ?? ''), city: hit.city ?? '', state: hit.state ?? '', type: hit.type ?? '', size: hit.beds != null ? hit.beds + ' beds' : '',
      beds: hit.beds, residents: null, amount: od ? money(od.per_year) : null, amountN: od ? Math.round(od.per_year / 1000) : 0, per: '', est: !!od?.census_estimated,
      list: 'search', listRank: 0, row: null, dims: null, warmth: 0, tier: (hit.tier?.value as Tier | undefined) ?? null, score: null, geo: hit.geo, ehr: hit.ehr,
    };
  }
  if (rec) {
    const f = rec.facility;
    const o = rec.opportunity as (CrmCompanyRecord['opportunity'] & { reimbursementAnnual?: number }) | null;
    return {
      key: id, name: tc(f.name ?? ''), city: f.city ?? '', state: f.state ?? '', type: f.type ?? '', size: f.beds != null ? f.beds + ' beds' : '',
      beds: f.beds, residents: null, amount: o ? money(o.opportunityAnnual) : null, amountN: o ? Math.round(o.opportunityAnnual / 1000) : 0,
      per: o?.reimbursementAnnual != null ? money(o.reimbursementAnnual) + ' Part B at the national rate' : '', est: !!o?.censusEstimated,
      list: 'search', listRank: 0, row: null, dims: null, warmth: 0, tier: (rec.scoring?.tier as Tier | undefined) ?? null, score: null, geo: null, ehr: null,
    };
  }
  return null;
}

/**
 * Targeting (design T6400-6601, salesVals L20260-20307 and L20684-20913): the rep's
 * territory. A header with Tier / Type filters, Find a facility and the view switch; the
 * metro's reimbursement strip; the ranked candidates (Priority, Uncaptured $, Skip for now);
 * four views (Map, Network, Ranked, Matrix); and the facility inspector with its six actions.
 * Every figure comes from the server (see useTargeting.ts). Sales, Super Admin and Operator
 * write (the server keeps deal writes to the owner); Partner reads.
 */
export function TargetingTab() {
  const { client } = useSession();
  const sales = useSales();
  const { params, setParam, canWrite, writeReason, setTab, openPrep, openDeal, reloadSignals } = sales;
  const tgView: View = (VIEWS as readonly string[]).includes(params.get('tgView') ?? '') ? (params.get('tgView') as View) : 'Map';
  const tgType = TYPES.includes(params.get('tgType') ?? '') ? (params.get('tgType') as string) : 'All';
  const tgTier = TIERS.includes(params.get('tgTier') ?? '') ? (params.get('tgTier') as string) : 'All';
  const listMode: ListMode = (LISTS as readonly string[]).includes(params.get('tgList') ?? '') ? (params.get('tgList') as ListMode) : 'Priority';
  const selParam = params.get('tgSel');

  const [q, setQ] = useState('');
  const [narrow, setNarrow] = useState(false);
  const [inspOpen, setInspOpen] = useState(false);
  const [netTip, setNetTip] = useState<string | null>(null);
  const [hits, setHitsSeen] = useState<Record<string, CatalogRow>>({});
  const [calls, setCalls] = useState<Record<string, string>>({});
  const [noted, setNoted] = useState<string | null>(null);
  const [wErr, setWErr] = useState<ApiError | null>(null);
  const mapRef = useRef<MapHandle>(null);

  // a signal (the shell's ticker) or a link sets tgSel: open that facility's panel
  useEffect(() => {
    if (selParam) setInspOpen(true);
  }, [selParam]);

  const searchHits = useFacilitySearch(q);

  // ── data ──
  const preSel = selParam;
  const data = useMetroData(params.get('tgMetro'));
  const board = data.board.data;
  const pros = useMemo(() => buildProspects(data.top.data, data.low.data, board?.facilities ?? []), [data.top.data, data.low.data, board]);
  const known = !!preSel && pros.some((p) => p.key === preSel);
  const metroReady = !!board && !!data.top.data;
  const outside = useOutsideFacility(preSel && metroReady && !hits[preSel] ? preSel : null, known);
  const typeOk = (p: Prospect) => tgType === 'All' || p.type === tgType;
  const tierOk = (p: Prospect) => tgTier === 'All' || p.tier === tgTier;
  const shown = pros.filter((p) => typeOk(p) && tierOk(p));
  const scored = shown.filter((p) => p.score !== null).sort((a, b) => (b.score as number) - (a.score as number));
  const extra = preSel && !known ? outsider(preSel, hits[preSel] ?? null, outside.data) : null;
  const sel: Prospect | null = (preSel ? pros.find((p) => p.key === preSel) : null) ?? extra ?? scored[0] ?? shown[0] ?? pros[0] ?? null;
  const selId = sel?.key ?? null;
  const [oppFor, setOppFor] = useState<string | null>(null);

  const detail = useSelectedData(selId, tgView === 'Network');
  const territory = detail.territory;
  const dealQ = detail.deal;
  const netQ = detail.net;

  const metro = metroLabel(board);
  const strip = stripOf(data.top.data);
  const terr = territory.data && territory.data.facility_id === selId ? territory.data : null;
  const w = terr?.team_warmth ?? sel?.warmth ?? 0;
  const ehr = terr ? terr.ehr : (sel?.ehr ?? null);
  const snoozed = !!terr?.mine?.snoozed_until;
  const openDealRow = dealQ.data?.deals?.find((d) => d.facility_id === selId && d.status === 'open') ?? null;
  const logAt = selId ? (calls[selId] ?? clock(terr?.mine?.last_call_at)) : '';

  // ── writes ──
  const run = useCallback(
    async (fn: () => Promise<unknown>, after: () => void) => {
      if (!canWrite) return;
      setWErr(null);
      try {
        await fn();
        after();
      } catch (e) {
        setWErr(e as ApiError);
      }
    },
    [canWrite],
  );
  const setWarmth = (id: string, cur: number, n: number) => {
    if (n === cur) return; // the server keeps warmth 1-5; it is changed, never cleared
    void run(
      () => tgWarmth(client, { params: { facilityId: id }, body: { warmth: n } }),
      () => {
        data.board.reload();
        if (id === selId) territory.reload();
        reloadSignals();
      },
    );
  };
  // Record EHR and Add note open the console's own dialog (never the browser's prompt);
  // the save goes through the same routes as before and an error stays in the dialog.
  const [editor, setEditor] = useState<{ kind: 'ehr' | 'note'; id: string; cur: string | null } | null>(null);
  const recordEhr = (id: string, cur: string | null) => {
    if (canWrite) setEditor({ kind: 'ehr', id, cur });
  };
  const addNote = (id: string) => {
    if (canWrite) setEditor({ kind: 'note', id, cur: null });
  };
  const saveEditor = async (text: string) => {
    if (!editor || !canWrite) return;
    const id = editor.id;
    if (editor.kind === 'ehr') {
      await tgEhr(client, { params: { facilityId: id }, body: { ehr: text || null } });
      data.board.reload();
      if (id === selId) territory.reload();
      reloadSignals();
    } else {
      await crmActivityCreate(client, { body: { type: 'note', facilityId: id, body: text } });
      setNoted(id);
    }
  };
  const pick = (k: string) => {
    setParam('tgSel', k);
    setInspOpen(true);
  };
  const dis = canWrite ? {} : { 'aria-disabled': true as const, title: writeReason ?? undefined };

  // ── the design's values ──
  const segs = (names: readonly string[], cur: string, key: string) =>
    names.map((n) => ({ name: n, style: seg(cur === n), onClick: () => setParam(key, n === names[0] && key !== 'tgView' ? null : n) }));

  const LISTROWS: Record<ListMode, Prospect[]> = {
    Priority: scored,
    'Uncaptured $': shown.filter((p) => p.list === 'top').sort((a, b) => b.amountN - a.amountN),
    'Skip for now': shown.filter((p) => p.list === 'skip'),
  };
  const maxK = Math.max(0, ...pros.filter((p) => p.list !== 'search').map((p) => p.amountN));
  const scaleK = maxK ? Math.ceil(maxK / 20) * 20 : 1;

  const pins: MapPin[] = useMemo(
    () =>
      shown
        .filter((p) => !!p.geo)
        .map((p) => ({
          k: p.key, name: p.name, lat: p.geo!.lat, lng: p.geo!.lng, amt: p.amountN, tier: p.tier, skip: p.list === 'skip', sel: p.key === selId,
          col: p.tier ? TC[p.tier] : p.list === 'skip' ? '#8A94A0' : '#4F8AD6', label: p.amount ? p.amount + '/yr' : 'not sized',
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pros, tgType, tgTier, selId],
  );

  const net: NetModel | null = useMemo(() => {
    if (!sel) return null;
    const n = netQ.data;
    const peers = pros.filter((p) => p.key !== sel.key && p.city === sel.city && p.list !== 'skip').slice(0, 4);
    const dealAt = openDealRow ? clock(openDealRow.created_at) : '';
    const nodes: NetNode[] = [
      ...(n?.payer_mix ?? []).map((m) => ({ id: 'pay:' + m.label, l: m.label, cls: 'pay' as const, w: m.pct, d: m.pct + '% of days · ' + n!.basis })),
      ...(n?.hospitals ?? []).slice(0, 3).map((h, i) => ({ id: 'ref:' + h.name, l: h.name, cls: 'ref' as const, w: 30 - i * 8, d: 'Likely referral source · ' + h.miles.toFixed(0) + ' mi · ' + n!.basis })),
      { id: 'sys:ehr', l: ehr || 'EHR not recorded', cls: 'sys', w: 10, dashed: !ehr, d: ehr ? 'Recorded by a rep' : 'Ask on the first call' },
      { id: 'sys:ai', l: 'Aithora', cls: 'us', w: 14, dashed: !openDealRow, d: openDealRow ? 'Deal started ' + dealAt : 'Prospect, no deal yet' },
      ...peers.map((p) => ({ id: 'peer:' + p.key, k: p.key, l: p.name.split(' ').slice(0, 3).join(' '), cls: 'peer' as const, w: 8, dashed: true, d: 'Same city · ' + (p.amount || 'not sized') })),
    ];
    return {
      key: sel.key + '|' + (ehr || '') + '|' + (openDealRow?.id || '') + '|' + (n ? 'n' : ''),
      name: sel.name.split(' ').slice(0, 4).join(' ').replace(/ (and|of|&)$/i, ''),
      tier: sel.tier,
      nodes,
    };
  }, [sel, pros, netQ.data, ehr, openDealRow]);

  const boardRows = scored; // the board is the scored set under the same filters (design board)
  const allScored = pros.filter((p) => p.score !== null);
  const tgBoardMeta = boardRows.length + ' of ' + allScored.length + ' facilities' + (tgTier === 'All' ? '' : ' · tier ' + tgTier) + (tgType === 'All' ? '' : ' · ' + tgType);
  const histMax = Math.max(0, ...strip.hist);
  const facN = strip.facilities;

  const v = {
    tgCity: metro,
    tgTiers: segs(TIERS, tgTier, 'tgTier'),
    tgTypes: segs(TYPES, tgType, 'tgType'),
    tgViews: VIEWS.map((n) => ({ name: n, style: seg(tgView === n), onClick: () => setParam('tgView', n) })),
    tgIsRanked: tgView === 'Ranked',
    tgIsMatrix: tgView === 'Matrix',
    tgIsMap: tgView === 'Map',
    tgIsNet: tgView === 'Network',
    tgSearch: q,
    tgOnSearch: (e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value),
    tgSearchHits: searchHits.map((h) => ({
      name: tc(h.name ?? ''),
      meta: [h.city, h.type].filter(Boolean).join(' · '),
      onClick: () => {
        setHitsSeen((m) => ({ ...m, [h.id]: h }));
        setQ('');
        pick(h.id);
      },
    })),
    tgListTabs: LISTS.map((n) => ({ name: n + ' ' + LISTROWS[n].length, style: seg(listMode === n) + 'height:24px;padding:0 9px;font-size:14px;', onClick: () => setParam('tgList', n === 'Priority' ? null : n) })),
    tgListNote: listMode === 'Priority' ? 'Priority 0–100, weighted across five dimensions' : listMode === 'Uncaptured $' ? 'Highest uncaptured Part B care-management dollars' : 'Smallest opportunity, estimated census. Park these.',
    tgRows: LISTROWS[listMode].map((p, i) => {
      const on = p.key === selId;
      const max = listMode === 'Priority' ? 100 : scaleK;
      const val = listMode === 'Priority' ? (p.score as number) : p.amountN;
      return {
        key: p.key,
        rank: String(i + 1),
        name: p.name,
        sub: [p.city, p.type, p.size].filter(Boolean).join(' · '),
        onClick: () => pick(p.key),
        right: listMode === 'Priority' ? String(p.score) : (p.amount ?? '–'),
        hasTier: !!p.tier,
        tier: p.tier || '',
        tierStyle: p.tier ? chip(TC[p.tier], 'rgba(34,39,46,.06)') : '',
        hasEst: !!p.est,
        estStyle: chip('#8A5A00', 'rgba(165,86,26,.12)'),
        bar:
          'display:block;height:3px;border-radius:2px;margin-top:5px;background:' + (p.tier ? TC[p.tier] : '#4F8AD6') + ';width:' + Math.max(4, (val / max) * 100) +
          '%;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + Math.min(300, i * 25) + 'ms;',
        style:
          'display:block;text-decoration:none;color:inherit;padding:8px 10px 8px 8px;border-top:1px solid rgba(34,39,46,.08);' +
          (on ? 'background:rgba(79,138,214,.10);box-shadow:inset 3px 0 0 #2F5C97;' : '') +
          (on && snoozed ? 'opacity:.5;' : ''),
      };
    }),
    tgNarrow: narrow,
    tgWide: !narrow,
    tgGridStyle:
      'flex:1 1 auto;min-height:0;position:relative;display:grid;gap:12px;grid-template-columns:' +
      (narrow ? 'minmax(190px,230px) minmax(0,1fr)' : 'minmax(230px,280px) minmax(0,1fr) minmax(250px,300px)') + ';',
    tgInspShow: !!sel && (!narrow || inspOpen),
    tgInspClose: () => setInspOpen(false),
    tgInspStyle:
      'min-width:0;min-height:0;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;gap:10px;' +
      (narrow
        ? 'position:absolute;right:0;top:0;bottom:0;width:min(300px,70%);z-index:30;background:#F7F3EA;padding:10px 12px;border-radius:10px;box-shadow:-10px 0 30px rgba(34,39,46,.18);animation:riseIn .25s cubic-bezier(.22,.61,.36,1);'
        : 'border-left:1px solid rgba(34,39,46,.12);padding-left:12px;'),
    tgNetTip: netTip || 'Hover a node to see the link. Click a neighbour building to open it.',
    tgNetEst: !!netQ.data && netQ.data.basis === 'EST',
    tgZoomIn: () => mapRef.current?.zoomBy(1.6),
    tgZoomOut: () => mapRef.current?.zoomBy(1 / 1.6),
    tgZoomFit: () => mapRef.current?.zoomBy(0),
    tgMapCount: pins.length + ' placed on the map · of the scored set' + (metro ? ' · ' + metro + ' metro' : ''),
    tgDots: scored
      .filter((p) => !!p.dims)
      .map((p) => {
        const x = axis(p.dims![2]),
          y = 100 - axis(p.dims![3]);
        const r = 12 + (p.score as number) / 8;
        const on = p.key === selId;
        return {
          key: p.key,
          name: p.name,
          onClick: () => pick(p.key),
          style:
            'position:absolute;left:' + x.toFixed(1) + '%;top:' + y.toFixed(1) +
            '%;transform:translate(-50%,-50%);display:flex;align-items:center;gap:6px;text-decoration:none;transition:left .6s cubic-bezier(.22,.61,.36,1),top .6s cubic-bezier(.22,.61,.36,1);z-index:' + (on ? 3 : 2) + ';',
          dot:
            'display:block;flex:0 0 auto;width:' + r + 'px;height:' + r + 'px;border-radius:50%;background:' + TC[p.tier as Tier] + ';border:2px solid #FCFAF4;box-shadow:0 0 0 ' +
            (on ? '3px rgba(47,92,151,.45)' : '1px rgba(34,39,46,.2)') + ';' + (on ? 'animation:pulse 2s ease-in-out infinite;' : ''),
          label: p.name.split(' ').slice(0, 3).join(' ') + ' · ' + p.score,
        };
      }),
    tgBoardMeta,
    tgBoard: boardRows.map((p) => {
      const pw = p.warmth;
      const pe = p.key === selId && terr ? terr.ehr : p.ehr;
      const dims = p.dims ?? [0, 0, 0, 0];
      return {
        key: p.key,
        name: p.name,
        meta: boardMeta(p),
        tier: 'Tier ' + p.tier,
        score: String(p.score),
        onPick: () => pick(p.key),
        scoreColor: (p.score as number) >= 70 ? '#12503C' : (p.score as number) >= 50 ? '#5B6B7C' : '#54606C',
        tierStyle: chip(TIER_TONE[p.tier as Tier][0], TIER_TONE[p.tier as Tier][1]),
        ehr: pe || 'EHR not recorded',
        ehrStyle: chip(pe ? '#264C7E' : '#54606C', pe ? 'rgba(79,138,214,.16)' : 'rgba(34,39,46,.07)'),
        dims: DIMS.map((d, i) => {
          const val = i === 4 ? pw * 20 : dims[i];
          return {
            k: d,
            v: i === 4 ? (pw ? pw + '/5' : 'not set') : String(val),
            bar: 'display:block;height:100%;border-radius:3px;width:' + Math.max(2, val) + '%;background:' + (i === 4 ? (pw ? '#1F6B52' : 'rgba(34,39,46,.14)') : val >= 70 ? '#1F6B52' : val >= 50 ? '#5B6B7C' : '#54606C') + ';',
          };
        }),
        warmth: [1, 2, 3, 4, 5].map((n) => ({ n: String(n), onClick: () => setWarmth(p.key, pw, n), style: warmBtn(n, pw, true) })),
        warmthNote: pw ? (pw >= 4 ? 'warm · worth a second call' : pw >= 2 ? 'lukewarm' : 'cold') : 'not set · raises the score once you rate it',
        onEhr: () => recordEhr(p.key, pe),
        ehrBtn: pe ? 'EHR: ' + pe : 'Record EHR',
        ehrBtnStyle: btn(!pe),
        onNote: () => addNote(p.key),
        noteBtn: noted === p.key ? 'Note saved' : 'Add note',
        noteBtnStyle: btn(false),
      };
    }),
    tgQuadrants: (
      [
        ['Strategic · harder', (d: number[]) => d[3] >= 65 && d[2] < 65, '#5B6B7C'],
        ['Strategic · easier', (d: number[]) => d[3] >= 65 && d[2] >= 65, '#1F6B52'],
        ['Lower value · harder', (d: number[]) => d[3] < 65 && d[2] < 65, '#54606C'],
        ['Lower value · easier', (d: number[]) => d[3] < 65 && d[2] >= 65, '#2F5C97'],
      ] as Array<[string, (d: number[]) => boolean, string]>
    ).map((qd, qi) => {
      const rows = boardRows.filter((p) => !!p.dims && qd[1](p.dims));
      return {
        name: qd[0],
        count: String(rows.length),
        countStyle: chip('#54606C', 'rgba(34,39,46,.08)'),
        wrap:
          'box-sizing:border-box;display:flex;padding:9px 11px;background:' + qd[2] + (qi === 1 ? '14' : '08') + ';' +
          (qi < 2 ? 'border-bottom:1px dashed rgba(34,39,46,.18);' : 'align-items:flex-end;') +
          (qi % 2 === 0 ? 'border-right:1px dashed rgba(34,39,46,.18);' : 'justify-content:flex-end;'),
      };
    }),
    tgScope: [facN != null ? facN + ' facilities' : '', metro.toUpperCase(), 'all types'].filter(Boolean).join(' · '),
    tgStats: (
      [
        ['Total uncaptured / yr', strip.total, '#1F6B52', 26],
        ['Avg / facility / yr', strip.avg, '#22272E', 19],
        ['Median / facility', strip.median, '#22272E', 19],
        ['Lowest', strip.min, '#54606C', 19],
        ['Highest', strip.max, '#22272E', 19],
      ] as Array<[string, number | null, string, number]>
    ).map((t) => ({ k: t[0], v: money(t[1]), figStyle: fig(t[2], t[3]) })),
    tgHist: strip.hist.map((h) => ({ style: 'flex:1;min-width:4px;border-radius:3px 3px 0 0;background:rgba(47,138,111,.55);height:' + Math.max(2, histMax ? (h / histMax) * 100 : 0) + '%;' })),
    tgHistL: money(strip.min),
    tgHistR: money(strip.max),
    tgHistCount: 'distribution' + (facN != null ? ' · ' + facN + ' facilities' : ''),
    tgHistNote:
      (tgType === 'All' ? '' : 'These five figures and the distribution are metro-wide across all types; the ' + tgType + ' filter narrows the two lists below, not the aggregates. ') +
      (strip.estimated != null ? strip.estimated + ' of these use an estimated census: assisted-living rows carry no bed data, and are shown with an EST tag. ' : '') +
      'Agencies without a census are excluded.',
    tgSel: sel
      ? {
          name: sel.name,
          sub: [sel.city ? sel.city + (sel.state ? ', ' + sel.state : '') : sel.state, sel.type, sel.size].filter(Boolean).join(' · '),
          hasTier: !!sel.tier,
          tier: 'Tier ' + (sel.tier || ''),
          tierStyle: sel.tier ? chip(TC[sel.tier], 'rgba(34,39,46,.07)') : '',
          hasScore: sel.score !== null,
          score: sel.score === null ? '' : String(sel.score),
          scoreColor: (sel.score ?? 0) >= 70 ? '#12503C' : (sel.score ?? 0) >= 50 ? '#2F5C97' : '#54606C',
          ring:
            'display:block;width:64px;height:64px;border-radius:50%;flex:0 0 64px;background:conic-gradient(' + (sel.tier ? TC[sel.tier] : '#4F8AD6') + ' ' + (sel.score || 0) * 3.6 +
            'deg, rgba(34,39,46,.09) 0);-webkit-mask:radial-gradient(circle 25px,transparent 98%,#000 100%);mask:radial-gradient(circle 25px,transparent 98%,#000 100%);transition:background .5s;',
          modeledStyle: chip('#264C7E', 'rgba(47,92,151,.12)'),
          dims: sel.dims
            ? DIMS.map((d, i) => {
                const val = i === 4 ? w * 20 : sel.dims![i];
                return {
                  k: d,
                  v: i === 4 ? (w ? w + '/5' : 'not set') : String(val),
                  bar:
                    'display:block;height:100%;border-radius:3px;width:' + Math.max(2, val) + '%;background:' + (val >= 70 ? '#1F6B52' : val >= 50 ? '#2F5C97' : '#8A94A0') +
                    ';transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 50 + 'ms;',
                };
              })
            : [],
          hasDims: !!sel.dims,
          noDims: !sel.dims,
          hasAmt: !!sel.amount,
          amount: sel.amount || '',
          per: sel.per,
          hasModeled: !!sel.amount || sel.score !== null,
          why: whyNow(sel, metro, w, ehr).map((x) => ({ t: x[0], hasTag: !!x[1], tag: x[1], tagStyle: x[1] === 'MODELED' ? chip('#264C7E', 'rgba(47,92,151,.12)') : chip('#8A5A00', 'rgba(165,86,26,.12)') })),
          warmth: [1, 2, 3, 4, 5].map((n) => ({ n: String(n), onClick: () => setWarmth(sel.key, w, n), style: warmBtn(n, w, false) })),
          warmthNote: warmNote(w),
          ehrBtn: ehr ? 'EHR: ' + ehr : 'Record EHR',
          onEhr: () => recordEhr(sel.key, ehr),
          hasDeal: !!openDealRow,
          dealLabel: openDealRow ? 'Deal started · open in CRM' : 'Start deal',
          onDeal: () => {
            if (openDealRow) {
              openDeal({ dealId: openDealRow.id });
              return;
            }
            void run(
              () => crmDealCreate(client, { body: { name: sel.name, facilityId: sel.key, type: 'facility', track: 'A', source: 'territory' } }),
              () => {
                dealQ.reload();
                reloadSignals();
              },
            );
          },
          onPrep: () => setTab('Sales Kit', { kitMode: 'lib', slDoc: 'Product One-Pager', kitPzQuery: sel.name }),
          onMp: () => openPrep(sel.key),
          logLabel: logAt ? 'Call logged ' + logAt : 'Log call',
          onLog: () =>
            void run(
              () =>
                crmFacilityCall(client, { params: { facilityId: sel.key }, body: {} }).then((r) =>
                  setCalls((m) => ({ ...m, [sel.key]: clock(r.activity?.occurred_at ?? r.activity?.created_at ?? new Date().toISOString()) })),
                ),
              () => reloadSignals(),
            ),
          snoozeLabel: snoozed ? 'Snoozed 7 days · undo' : 'Snooze 7 days',
          onSnooze: () =>
            void run(
              () => tgSnooze(client, { params: { facilityId: sel.key }, body: { days: snoozed ? 0 : 7 } }),
              () => {
                territory.reload();
                reloadSignals();
              },
            ),
          primary: btn(true) + 'height:32px;',
          ghost: btn(false) + 'height:32px;',
        }
      : null,
  };

  // the grid narrows under 1000px: the inspector becomes an overlay (design tgGridRef)
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const chkW = () => setNarrow(el.clientWidth > 0 && el.clientWidth < 1000); // 0 = not laid out yet
    chkW();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(chkW);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const loadErr = data.board.error && !board ? data.board.error : data.top.error && !data.top.data ? data.top.error : null;
  const busy = (data.board.loading && !board) || (!!board && data.top.loading && !data.top.data);
  const s = v.tgSel;

  return (
    <div data-screen-label="Territory" data-testid="sales-targeting" aria-busy={busy ? 'true' : undefined} style={sx('height:100%;min-height:0;display:flex;flex-direction:column;gap:8px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Territory</span>{' '}
        <span style={sx('font-size:16px;font-weight:600;color:#3C454F;')}>
          <span>{v.tgCity}</span>
        </span>{' '}
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-left:6px;')}>Tier</span>{' '}
        <div role="group" aria-label="Tier" style={sx('display:flex;align-items:center;gap:2px;border:1px solid rgba(34,39,46,.14);border-radius:8px;padding:2px;')}>
          {v.tgTiers.map((x) => (
            <Fragment key={x.name}>
              <a href="#" aria-pressed={x.name === tgTier} onClick={ck(x.onClick)} style={sx(x.style)}>
                <span>{x.name}</span>
              </a>
            </Fragment>
          ))}
        </div>{' '}
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Type</span>{' '}
        <div role="group" aria-label="Type" style={sx('display:flex;align-items:center;gap:2px;border:1px solid rgba(34,39,46,.14);border-radius:8px;padding:2px;')}>
          {v.tgTypes.map((x) => (
            <Fragment key={x.name}>
              <a href="#" aria-pressed={x.name === tgType} onClick={ck(x.onClick)} style={sx(x.style)}>
                <span>{x.name}</span>
              </a>
            </Fragment>
          ))}
        </div>{' '}
        <span style={sx('position:relative;flex:1 1 180px;min-width:160px;max-width:300px;')}>
          <input
            type="search"
            value={v.tgSearch}
            onChange={v.tgOnSearch}
            placeholder="Find a facility"
            aria-label="Find a facility"
            style={sx('width:100%;box-sizing:border-box;height:30px;padding:0 11px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FCFAF4;font-size:16px;font-family:inherit;color:#22272E;')}
          />{' '}
          <span style={sx('position:absolute;left:0;right:0;top:34px;z-index:20;display:flex;flex-direction:column;background:#FCFAF4;border-radius:8px;box-shadow:0 8px 24px rgba(34,39,46,.14);overflow:hidden;')}>
            {v.tgSearchHits.map((h, h_i) => (
              <Fragment key={h_i}>
                <a href="#" onClick={ck(h.onClick)} style={sx('display:flex;flex-direction:column;padding:7px 11px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('font-size:16px;font-weight:700;')}>
                    <span>{h.name}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{h.meta}</span>
                  </span>
                </a>
              </Fragment>
            ))}
          </span>
        </span>{' '}
        <span style={sx('flex:1;')} />{' '}
        <div role="group" aria-label="View" style={sx('display:flex;align-items:center;gap:2px;border:1px solid rgba(34,39,46,.14);border-radius:8px;padding:2px;')}>
          {v.tgViews.map((x) => (
            <Fragment key={x.name}>
              <a href="#" aria-pressed={x.name === tgView} onClick={ck(x.onClick)} style={sx(x.style)}>
                <span>{x.name}</span>
              </a>
            </Fragment>
          ))}
        </div>
      </div>{' '}
      <div style={sx('flex:0 0 auto;display:flex;align-items:flex-end;gap:10px 22px;flex-wrap:wrap;padding:8px 0 9px;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.1);')}>
        <span
          style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;')}
          title={OPPORTUNITY_HEADLINE_NOTE}
        >
          <span style={sx('display:flex;align-items:center;gap:7px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;')} />
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Reimbursement opportunity</span>
            {/* The opportunity route's rows are always modeled (`modeled: true`). */}
            {(data.top.data?.facilities[0]?.modeled ?? true) && (
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.07em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:5px;padding:1px 6px;')}>MODELED</span>
            )}
          </span>{' '}
          <span style={sx('font-size:14px;color:#54606C;')}>
            <span>{v.tgScope}</span>
          </span>
        </span>{' '}
        {v.tgStats.map((t, t_i) => (
          <Fragment key={t_i}>
            <span style={sx('display:flex;flex-direction:column;gap:4px;min-width:0;')}>
              <span style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:600;white-space:nowrap;')}>
                <span>{t.k}</span>
              </span>
              <span style={sx(t.figStyle)}>
                <span>{t.v}</span>
              </span>
            </span>
          </Fragment>
        ))}{' '}
        <span style={sx('flex:1 1 160px;min-width:150px;max-width:260px;display:flex;flex-direction:column;gap:3px;')} title={v.tgHistNote}>
          <span data-testid="tg-hist" style={sx('display:flex;align-items:flex-end;gap:2px;height:30px;')}>
            {v.tgHist.map((b, b_i) => (
              <Fragment key={b_i}>
                <span style={sx(b.style)} />
              </Fragment>
            ))}
          </span>{' '}
          <span style={sx('display:flex;gap:6px;font-size:14px;color:#54606C;min-width:0;')}>
            <span style={sx('flex:0 0 auto;')}>
              <span>{v.tgHistL}</span>
            </span>
            <span style={sx('flex:1 1 auto;min-width:0;text-align:center;overflow-wrap:break-word;')}>{v.tgHistCount}</span>
            <span style={sx('flex:0 0 auto;')}>
              <span>{v.tgHistR}</span>
            </span>
          </span>
        </span>
      </div>{' '}
      {loadErr && <ErrorNote error={loadErr} onRetry={() => (data.board.error ? data.board.reload() : data.top.reload())} compact />}
      <div ref={gridRef} style={sx(v.tgGridStyle)}>
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;')}>
          <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px;margin-bottom:6px;')}>
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Ranked candidates</span>
          </div>{' '}
          <div role="group" aria-label="Candidate list" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:3px;flex-wrap:wrap;margin-bottom:5px;')}>
            {v.tgListTabs.map((x, x_i) => (
              <Fragment key={x_i}>
                <a href="#" onClick={ck(x.onClick)} style={sx(x.style)}>
                  <span>{x.name}</span>
                </a>
              </Fragment>
            ))}
          </div>{' '}
          <div style={sx('flex:0 0 auto;font-size:14px;color:#54606C;margin-bottom:4px;')}>
            <span>{v.tgListNote}</span>
          </div>{' '}
          <div data-testid="tg-rows" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;')}>
            {v.tgRows.map((r) => (
              <Fragment key={r.key}>
                <a href="#" onClick={ck(r.onClick)} style={sx(r.style)}>
                  <span style={sx('display:flex;align-items:center;gap:8px;min-width:0;')}>
                    <span style={sx('flex:0 0 18px;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                      <span>{r.rank}</span>
                    </span>{' '}
                    <span style={sx('flex:1 1 auto;min-width:0;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;overflow-wrap:break-word;')}>
                        <span>{r.name}</span>
                      </span>{' '}
                      <span style={sx('display:flex;align-items:center;gap:5px;font-size:14px;color:#54606C;margin-top:1px;min-width:0;')}>
                        <span style={sx('overflow-wrap:break-word;min-width:0;')}>
                          <span>{r.sub}</span>
                        </span>
                        {r.hasEst && <span style={sx(r.estStyle)}>EST</span>}
                      </span>
                    </span>{' '}
                    {r.hasTier && (
                      <span style={sx(r.tierStyle)}>
                        <span>{r.tier}</span>
                      </span>
                    )}{' '}
                    <span style={sx('flex:0 0 auto;font-size:16px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>
                      <span>{r.right}</span>
                    </span>
                  </span>{' '}
                  <span style={sx('display:block;margin-left:26px;')}>
                    <span style={sx(r.bar)} />
                  </span>
                </a>
              </Fragment>
            ))}
          </div>
        </div>{' '}
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;position:relative;')}>
          {v.tgIsMap && (
            <div style={sx('flex:1 1 auto;min-height:0;position:relative;border:1px solid rgba(34,39,46,.12);border-radius:10px;overflow:hidden;background:#F4EFE3;')}>
              <MapView
                pins={pins}
                counties={board?.meta.counties ?? null}
                fitCounties={board?.meta.fit_counties ?? null}
                state={board?.meta.state ?? pros.find((p) => p.state)?.state ?? null}
                onPick={pick}
                handle={mapRef}
              />{' '}
              <div style={sx('position:absolute;left:10px;top:10px;display:flex;flex-direction:column;gap:5px;padding:7px 10px;background:rgba(252,250,244,.92);border-radius:8px;pointer-events:none;')}>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>Uncaptured $ by county</span>{' '}
                <span style={sx('display:flex;align-items:center;gap:3px;font-size:14px;color:#3C454F;')}>
                  <span style={sx('width:14px;height:9px;background:#EFE9DC;border:1px solid #D5CBB5;')} />
                  none
                  <span style={sx('width:14px;height:9px;background:#E3EAF3;margin-left:4px;')} />
                  <span style={sx('width:14px;height:9px;background:#CFDCEC;')} />
                  <span style={sx('width:14px;height:9px;background:#BACDE5;')} />
                  <span style={sx('width:14px;height:9px;background:#A6BFDD;')} />
                  <span style={sx('width:14px;height:9px;background:#93B1D6;')} />
                  $3M+
                </span>{' '}
                <span style={sx('display:flex;align-items:center;gap:9px;font-size:14px;color:#3C454F;')}>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#1F6B52;')} />A
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#2F5C97;')} />B
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#5B6B7C;')} />C
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#4F8AD6;')} />
                    not scored
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;border:1.5px solid #8A94A0;box-sizing:border-box;')} />
                    skip
                  </span>
                </span>
              </div>{' '}
              <div style={sx('position:absolute;right:10px;top:10px;display:flex;flex-direction:column;gap:5px;')}>
                <a href="#" onClick={ck(v.tgZoomIn)} aria-label="Zoom in" style={sx('display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);font-size:17px;font-weight:700;color:#22272E;text-decoration:none;')}>
                  +
                </a>{' '}
                <a href="#" onClick={ck(v.tgZoomOut)} aria-label="Zoom out" style={sx('display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);font-size:17px;font-weight:700;color:#22272E;text-decoration:none;')}>
                  &#8722;
                </a>{' '}
                <a href="#" onClick={ck(v.tgZoomFit)} aria-label="Fit metro" style={sx('display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);font-size:16px;color:#22272E;text-decoration:none;')}>
                  &#10530;
                </a>
              </div>{' '}
              <div style={sx('position:absolute;left:10px;right:10px;bottom:8px;display:flex;align-items:center;gap:10px;pointer-events:none;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:rgba(34,39,46,.5);')}>
                <span style={sx('min-width:0;overflow-wrap:break-word;')}>
                  <span>{v.tgMapCount}</span>
                </span>
                <span style={sx('flex:1;')} />
                {v.tgWide && <span style={sx('flex:0 1 auto;min-width:0;overflow-wrap:break-word;')}>Pins by geocode &#183; scroll zoom &#183; drag pan</span>}
              </div>
            </div>
          )}{' '}
          {v.tgIsNet && (
            <div style={sx('flex:1 1 auto;min-height:0;position:relative;border:1px solid rgba(34,39,46,.12);border-radius:10px;overflow:hidden;')}>
              {net && <NetView net={net} onTip={setNetTip} onPick={pick} />}{' '}
              <div style={sx('position:absolute;left:10px;top:10px;display:flex;flex-direction:column;gap:5px;padding:7px 10px;background:rgba(252,250,244,.92);border-radius:8px;pointer-events:none;')}>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>Who this facility runs on</span>{' '}
                <span style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;font-size:14px;color:#3C454F;')}>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#1F6B52;')} />
                    Who pays
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#2F5C97;')} />
                    Referrals in
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#5B6B7C;')} />
                    Systems
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;background:#6B5BB8;')} />
                    Nearby prospects
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:4px;')}>
                    <span style={sx('width:10px;height:10px;border-radius:50%;border:1.5px dashed #54606C;box-sizing:border-box;')} />
                    not confirmed
                  </span>
                </span>
              </div>{' '}
              <div style={sx('position:absolute;left:10px;right:10px;bottom:8px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;pointer-events:none;')}>
                <span style={sx('font-size:16px;color:#22272E;background:rgba(252,250,244,.92);border-radius:6px;padding:4px 8px;')}>
                  <span>{v.tgNetTip}</span>
                </span>
                <span style={sx('flex:1;')} />
                {v.tgNetEst && (
                  <span style={sx('font-size:14px;font-weight:700;letter-spacing:.07em;color:#8A5A00;background:rgba(165,86,26,.12);border-radius:5px;padding:2px 7px;white-space:nowrap;')}>PAYER MIX + REFERRALS EST</span>
                )}
              </div>
            </div>
          )}{' '}
          {v.tgIsRanked && (
            <>
              <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:4px;margin-bottom:6px;')}>
                <span style={sx('font-size:16px;font-weight:700;')}>Pilot prioritization</span>{' '}
                <span style={sx('font-size:16px;color:#54606C;')}>Priority score 0&ndash;100 across five dimensions. Set warmth after a call.</span> <span style={sx('flex:1;')} />{' '}
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{v.tgBoardMeta}</span>
                </span>
              </div>{' '}
              <div data-testid="tg-board" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;')}>
                {v.tgBoard.map((f) => (
                  <Fragment key={f.key}>
                    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 4px 11px;margin-bottom:0;')}>
                      <div style={sx('display:flex;align-items:flex-start;gap:14px;')}>
                        <span style={sx('flex:0 1 auto;min-width:0;text-align:center;')}>
                          <span style={sx(`display:block;font-size:26px;font-weight:700;line-height:1;color:${f.scoreColor};`)}>
                            <span>{f.score}</span>
                          </span>{' '}
                          <span style={sx('display:block;font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:4px;')}>Priority</span>
                        </span>{' '}
                        <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                          <span style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
                            <span style={sx(f.tierStyle)}>
                              <span>{f.tier}</span>
                            </span>{' '}
                            <a href="#" onClick={ck(f.onPick)} style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;text-decoration:none;')}>
                              <span>{f.name}</span>
                            </a>{' '}
                            <span style={sx(f.ehrStyle)}>
                              <span>{f.ehr}</span>
                            </span>
                          </span>{' '}
                          <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;')}>
                            <span>{f.meta}</span>
                          </span>{' '}
                          <span style={sx('display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;margin-top:8px;')}>
                            {f.dims.map((d, d_i) => (
                              <Fragment key={d_i}>
                                <span style={sx('min-width:0;')}>
                                  <span style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;')}>
                                    <span style={sx('font-size:14px;color:#54606C;min-width:min(100%,130px);flex:1 1 130px;')}>
                                      <span>{d.k}</span>
                                    </span>{' '}
                                    <span style={sx('font-size:14px;font-weight:700;color:#22272E;')}>
                                      <span>{d.v}</span>
                                    </span>
                                  </span>{' '}
                                  <span style={sx('display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);margin-top:5px;')}>
                                    <span style={sx(d.bar)} />
                                  </span>
                                </span>
                              </Fragment>
                            ))}
                          </span>{' '}
                          <span style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-top:8px;row-gap:6px;')}>
                            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Warmth</span>{' '}
                            {f.warmth.map((wb, w_i) => (
                              <Fragment key={w_i}>
                                <a href="#" {...dis} aria-label={'Warmth ' + wb.n + ': ' + f.name} onClick={ck(wb.onClick)} style={sx(wb.style)}>
                                  <span>{wb.n}</span>
                                </a>
                              </Fragment>
                            ))}{' '}
                            <span style={sx('font-size:16px;color:#54606C;')}>
                              <span>{f.warmthNote}</span>
                            </span>{' '}
                            <span style={sx('flex:1;')} />{' '}
                            <a href="#" {...dis} onClick={ck(f.onEhr)} style={sx(f.ehrBtnStyle)}>
                              <span>{f.ehrBtn}</span>
                            </a>{' '}
                            <a href="#" {...dis} onClick={ck(f.onNote)} style={sx(f.noteBtnStyle)}>
                              <span>{f.noteBtn}</span>
                            </a>
                          </span>
                        </span>
                      </div>
                    </div>
                  </Fragment>
                ))}
              </div>
            </>
          )}{' '}
          {v.tgIsMatrix && (
            <>
              <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:4px;margin-bottom:6px;')}>
                <span style={sx('font-size:16px;font-weight:700;')}>Ease &times; Strategic</span>{' '}
                <span style={sx('font-size:16px;color:#54606C;')}>Work the top-right quadrant first: easy to land and worth landing.</span> <span style={sx('flex:1;')} />{' '}
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{v.tgBoardMeta}</span>
                </span>
              </div>{' '}
              <div style={sx('flex:1 1 auto;min-height:0;display:grid;grid-template-columns:22px minmax(0,1fr);grid-template-rows:minmax(0,1fr) 20px;gap:6px;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;writing-mode:vertical-rl;transform:rotate(180deg);text-align:center;')}>Strategic &#8594;</span>{' '}
                <div style={sx('position:relative;min-height:0;border:1px solid rgba(34,39,46,.12);border-radius:10px;overflow:hidden;')}>
                  <div style={sx('position:absolute;inset:0;display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;')}>
                    {v.tgQuadrants.map((qd, qd_i) => (
                      <Fragment key={qd_i}>
                        <div style={sx(qd.wrap)}>
                          <span style={sx('display:flex;align-items:center;gap:7px;')}>
                            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
                              <span>{qd.name}</span>
                            </span>
                            <span style={sx(qd.countStyle)}>
                              <span>{qd.count}</span>
                            </span>
                          </span>
                        </div>
                      </Fragment>
                    ))}
                  </div>{' '}
                  {v.tgDots.map((d) => (
                    <Fragment key={d.key}>
                      <a href="#" onClick={ck(d.onClick)} title={d.name} style={sx(d.style)}>
                        <span style={sx(d.dot)} />
                        <span style={sx('font-size:14px;font-weight:700;color:#22272E;white-space:nowrap;background:rgba(252,250,244,.85);border-radius:4px;padding:1px 4px;')}>
                          <span>{d.label}</span>
                        </span>
                      </a>
                    </Fragment>
                  ))}
                </div>{' '}
                <span /> <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;text-align:center;')}>Easier to land &#8594;</span>
              </div>
            </>
          )}
        </div>{' '}
        {v.tgInspShow && s && (
          <div data-testid="tg-inspector" style={sx(v.tgInspStyle)}>
            {v.tgNarrow && (
              <a href="#" onClick={ck(v.tgInspClose)} aria-label="Close facility panel" style={sx('align-self:flex-end;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.18);font-size:16px;color:#22272E;text-decoration:none;')}>
                &#10005;
              </a>
            )}{' '}
            <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
              <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Facility</span>
                {s.hasTier && (
                  <span style={sx(s.tierStyle)}>
                    <span>{s.tier}</span>
                  </span>
                )}
              </span>{' '}
              <span style={sx('font-size:18px;font-weight:800;color:#22272E;line-height:1.2;text-wrap:pretty;')}>
                <span>{s.name}</span>
              </span>{' '}
              <span style={sx('font-size:16px;color:#54606C;')}>
                <span>{s.sub}</span>
              </span>
            </div>{' '}
            <div style={sx('display:flex;align-items:center;gap:14px;')}>
              {s.hasScore && (
                <span style={sx('position:relative;display:flex;align-items:center;justify-content:center;width:64px;height:64px;flex:0 0 64px;')}>
                  <span style={sx(s.ring)} />
                  <span style={sx('position:absolute;display:flex;flex-direction:column;align-items:center;')}>
                    <span style={sx(`font-size:20px;font-weight:800;color:${s.scoreColor};line-height:1;`)}>
                      <span>{s.score}</span>
                    </span>
                    <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;color:#54606C;')}>PRIORITY</span>
                  </span>
                </span>
              )}{' '}
              <span style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;')}>
                {s.hasAmt && (
                  <>
                    <span style={sx('font-size:22px;font-weight:800;color:#1F6B52;line-height:1;')} title={OPPORTUNITY_HEADLINE_NOTE}>
                      <span>{s.amount}</span>
                      <span style={sx('font-size:14px;font-weight:600;color:#54606C;')}> /yr uncaptured</span>
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{s.per}</span>
                    </span>
                    {/* Restored from production: the code-by-code breakdown and its PDF. */}
                    <a href="#" data-testid="tg-opp-open" onClick={(e) => { e.preventDefault(); if (selId) setOppFor(selId); }} style={sx('font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;')}>
                      Code breakdown
                    </a>
                  </>
                )}{' '}
                {s.hasModeled && <BuildLabel><span style={sx(`${s.modeledStyle}align-self:flex-start;`)}>MODELED</span></BuildLabel>}
              </span>
            </div>{' '}
            {s.hasDims && (
              <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
                {s.dims.map((d, d_i) => (
                  <Fragment key={d_i}>
                    <span style={sx('display:grid;grid-template-columns:70px minmax(0,1fr) 44px;align-items:center;gap:8px;')}>
                      <span style={sx('font-size:14px;color:#3C454F;')}>
                        <span>{d.k}</span>
                      </span>
                      <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.08);')}>
                        <span style={sx(d.bar)} />
                      </span>
                      <span style={sx('font-size:14px;font-weight:700;color:#22272E;text-align:right;')}>
                        <span>{d.v}</span>
                      </span>
                    </span>
                  </Fragment>
                ))}
              </div>
            )}{' '}
            {s.noDims && <div style={sx('font-size:16px;color:#54606C;text-wrap:pretty;')}>Not scored yet. The five-dimension score appears once the facility is in a metro the engine scores.</div>}{' '}
            <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Warmth</span>
              {s.warmth.map((x, x_i) => (
                <Fragment key={x_i}>
                  <a href="#" {...dis} aria-label={'Warmth ' + x.n} onClick={ck(x.onClick)} style={sx(x.style)}>
                    <span>{x.n}</span>
                  </a>
                </Fragment>
              ))}
              <span style={sx('font-size:14px;color:#54606C;')}>
                <span>{s.warmthNote}</span>
              </span>
            </div>{' '}
            <div style={sx('display:grid;grid-template-columns:1fr 1fr;gap:6px;')}>
              <a href="#" {...(s.hasDeal ? {} : dis)} onClick={ck(s.onDeal)} style={sx(s.primary)}>
                <span>{s.dealLabel}</span>
              </a>{' '}
              <a href="#" onClick={ck(s.onPrep)} style={sx(s.ghost)}>
                Prep one-pager
              </a>{' '}
              <a href="#" onClick={ck(s.onMp)} style={sx(s.ghost)}>
                Prep for meeting
              </a>{' '}
              <a href="#" {...dis} onClick={ck(s.onLog)} style={sx(s.ghost)}>
                <span>{s.logLabel}</span>
              </a>{' '}
              <a href="#" {...dis} onClick={ck(s.onSnooze)} style={sx(s.ghost)}>
                <span>{s.snoozeLabel}</span>
              </a>{' '}
              <a href="#" {...dis} onClick={ck(s.onEhr)} style={sx(`${s.ghost}grid-column:1 / -1;`)}>
                <span>{s.ehrBtn}</span>
              </a>
            </div>{' '}
            {wErr && <ErrorNote error={wErr} compact />}
            <div style={sx('display:flex;flex-direction:column;gap:0;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:4px;')}>Why now</span>{' '}
              {s.why.map((x, x_i) => (
                <Fragment key={x_i}>
                  <span style={sx('display:flex;align-items:flex-start;gap:7px;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);font-size:16px;color:#22272E;line-height:1.4;text-wrap:pretty;')}>
                    <span style={sx('flex:1 1 auto;min-width:0;')}>
                      <span>{x.t}</span>
                    </span>
                    {x.hasTag && (
                      <BuildTagged tag={x.tag}>
                      <span style={sx(x.tagStyle)}>
                        <span>{x.tag}</span>
                      </span>
                      </BuildTagged>
                    )}
                  </span>
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
      {oppFor && <OpportunityBreakdown facilityId={oppFor} onClose={() => setOppFor(null)} />}
      {editor && (
        <ConfirmDialog
          title={editor.kind === 'ehr' ? 'Record the EHR' : 'Add a note'}
          body={
            editor.kind === 'ehr'
              ? 'Which EHR does this facility run? Leave it empty to clear it.'
              : 'The note is saved to this facility in the CRM.'
          }
          confirmLabel={editor.kind === 'ehr' ? 'Save EHR' : 'Save note'}
          reason={
            editor.kind === 'ehr'
              ? { label: 'EHR', placeholder: 'For example PointClickCare', required: !editor.cur, initial: editor.cur ?? '' }
              : { label: 'Note', placeholder: 'What happened, or what to do next' }
          }
          onConfirm={saveEditor}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

